import { useEffect, useRef, useState, type KeyboardEvent, type PointerEvent as ReactPointerEvent } from 'react';
import { Icon } from '../lib/icons';
import { useAuth } from '../lib/auth';
import { initials } from '../lib/session';
import { phoneWords, pictureFiles, pictureUrl, usePictureOf, type Crop, type PhonePicture, type PictureSize } from '../lib/pictures';
import { usePhone } from './PhoneApp';
import { Button, Notice } from './ui';

/**
 * Someone's face: their picture when they have one, else their initials. Decorative (their name is
 * always next to it).
 */
export function Face({ userId, name, size = 'medium', className = 'avatar round' }: { userId: string | null | undefined; name: string; size?: PictureSize; className?: string }) {
  const version = usePictureOf(userId);
  return (
    <span className={`${className}${version ? ' has-picture' : ''}`} aria-hidden="true">
      {version && userId ? <img src={pictureUrl(userId, version, size)} alt="" loading="lazy" decoding="async" /> : initials(name)}
    </span>
  );
}

/** The signed-in person's picture inside a round button of the app's top bar (else their initials). */
export function YouPicture({ name }: { name: string }) {
  const { state } = useAuth();
  const id = state?.user?.id;
  const version = usePictureOf(id);
  return version && id ? <img className="you-picture" src={pictureUrl(id, version, 'small')} alt="" decoding="async" /> : <>{initials(name)}</>;
}

/** The square on screen the picture is cropped in (CSS pixels). */
const VIEW = 260;
const MAX_ZOOM = 4;

/**
 * Choosing a picture and cropping it to a circle: drag to move it, the slider (or the mouse wheel,
 * or + and -) to zoom, the arrow keys to nudge it. Saves a square; Stitch shows it in a circle.
 */
export function Cropper({ src, busy, onCancel, onSave }: { src: string; busy: boolean; onCancel: () => void; onSave: (img: HTMLImageElement, crop: Crop) => void }) {
  const [img, setImg] = useState<HTMLImageElement | null>(null);
  const [failed, setFailed] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const drag = useRef<{ id: number; x: number; y: number; from: { x: number; y: number } } | null>(null);

  useEffect(() => {
    const i = new Image();
    i.onload = () => {
      const base = VIEW / Math.min(i.naturalWidth, i.naturalHeight);
      setImg(i);
      setZoom(1);
      setPos({ x: (VIEW - i.naturalWidth * base) / 2, y: (VIEW - i.naturalHeight * base) / 2 });
    };
    i.onerror = () => setFailed(true);
    i.src = src;
  }, [src]);

  if (failed) {
    return (
      <div className="crop">
        <Notice tone="warn">That picture can’t be opened here. Choose a JPEG or PNG photo.</Notice>
        <Button variant="ghost" onClick={onCancel}>
          Back
        </Button>
      </div>
    );
  }
  if (!img) return <div className="crop crop-loading">Opening the picture…</div>;

  const base = VIEW / Math.min(img.naturalWidth, img.naturalHeight);
  const scale = base * zoom;
  const w = img.naturalWidth * scale;
  const h = img.naturalHeight * scale;
  // The picture always covers the circle's square.
  const clamp = (p: { x: number; y: number }, sw = w, sh = h) => ({ x: Math.min(0, Math.max(VIEW - sw, p.x)), y: Math.min(0, Math.max(VIEW - sh, p.y)) });
  const zoomTo = (z: number) => {
    const next = Math.min(MAX_ZOOM, Math.max(1, z));
    const k = next / zoom;
    // Zoom around the middle of the circle.
    const c = VIEW / 2;
    setPos(clamp({ x: c - (c - pos.x) * k, y: c - (c - pos.y) * k }, img.naturalWidth * base * next, img.naturalHeight * base * next));
    setZoom(next);
  };
  const down = (e: ReactPointerEvent<HTMLDivElement>) => {
    e.currentTarget.setPointerCapture(e.pointerId);
    drag.current = { id: e.pointerId, x: e.clientX, y: e.clientY, from: pos };
  };
  const move = (e: ReactPointerEvent<HTMLDivElement>) => {
    const d = drag.current;
    if (!d || d.id !== e.pointerId) return;
    setPos(clamp({ x: d.from.x + e.clientX - d.x, y: d.from.y + e.clientY - d.y }));
  };
  const up = () => {
    drag.current = null;
  };
  const key = (e: KeyboardEvent<HTMLDivElement>) => {
    const step = e.shiftKey ? 30 : 8;
    const moves: Record<string, [number, number]> = { ArrowLeft: [step, 0], ArrowRight: [-step, 0], ArrowUp: [0, step], ArrowDown: [0, -step] };
    if (moves[e.key]) {
      e.preventDefault();
      setPos(clamp({ x: pos.x + moves[e.key]![0], y: pos.y + moves[e.key]![1] }));
    } else if (e.key === '+' || e.key === '=') {
      e.preventDefault();
      zoomTo(zoom + 0.2);
    } else if (e.key === '-') {
      e.preventDefault();
      zoomTo(zoom - 0.2);
    }
  };
  const crop: Crop = { x: -pos.x / scale, y: -pos.y / scale, size: VIEW / scale };
  return (
    <div className="crop">
      <div
        className="crop-view"
        style={{ width: VIEW, height: VIEW }}
        tabIndex={0}
        role="img"
        aria-label="Your picture, in a circle. Drag it, or use the arrow keys, to move it."
        onPointerDown={down}
        onPointerMove={move}
        onPointerUp={up}
        onPointerCancel={up}
        onWheel={(e) => zoomTo(zoom - Math.sign(e.deltaY) * 0.15)}
        onKeyDown={key}
      >
        <img src={src} alt="" draggable={false} style={{ left: pos.x, top: pos.y, width: w, height: h }} />
        <span className="crop-ring" aria-hidden="true" />
      </div>
      <label className="crop-zoom">
        <Icon name="search" size={15} />
        <span className="sr-only">Zoom</span>
        <input type="range" min={1} max={MAX_ZOOM} step={0.01} value={zoom} onChange={(e) => zoomTo(Number(e.target.value))} aria-label="Zoom" />
      </label>
      <div className="crop-actions">
        <Button variant="ghost" onClick={onCancel} disabled={busy}>
          Cancel
        </Button>
        <Button variant="primary" icon="check" onClick={() => onSave(img, crop)} disabled={busy}>
          {busy ? 'Saving…' : 'Save photo'}
        </Button>
      </div>
    </div>
  );
}

/**
 * A person's photo: shown large, with Add (choose one, or take one on a phone), Change and Remove.
 * Choosing one opens the cropper in place. Saving makes the three sizes here and hands them over.
 */
export function PhotoEditor({
  userId,
  name,
  whose,
  save,
  remove,
  phones,
  onRetry,
  autoFocus
}: {
  userId: string;
  name: string;
  whose: 'your' | 'their';
  /** Saves the three sizes (throws with words for the person). */
  save: (files: Record<PictureSize, string>) => Promise<void>;
  remove?: () => Promise<void>;
  /** How it is on the desk phones, after the last save. */
  phones?: PhonePicture[];
  onRetry?: () => Promise<void>;
  autoFocus?: boolean;
}) {
  const version = usePictureOf(userId);
  const phone = usePhone();
  const choose = useRef<HTMLInputElement | null>(null);
  const camera = useRef<HTMLInputElement | null>(null);
  const [src, setSrc] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState('');
  useEffect(() => () => void (src && URL.revokeObjectURL(src)), [src]);

  const picked = (files: FileList | null) => {
    const f = files?.[0];
    if (!f) return;
    setProblem('');
    if (f.size > 30 * 1024 * 1024) return setProblem('That picture is over 30 MB. Choose a smaller one.');
    setSrc(URL.createObjectURL(f));
  };
  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    setProblem('');
    try {
      await fn();
    } catch (err) {
      setProblem(err instanceof Error ? err.message : 'That didn’t work. Try again.');
    } finally {
      setBusy(false);
    }
  };
  const words = phoneWords(phones ?? [], whose);
  const inputs = (
    <>
      <input ref={choose} type="file" accept="image/*" hidden onChange={(e) => (picked(e.target.files), (e.target.value = ''))} />
      {phone && <input ref={camera} type="file" accept="image/*" capture="user" hidden onChange={(e) => (picked(e.target.files), (e.target.value = ''))} />}
    </>
  );

  if (src) {
    return (
      <div className="photo-editor">
        <Cropper
          src={src}
          busy={busy}
          onCancel={() => setSrc(null)}
          onSave={(img, crop) =>
            void run(async () => {
              await save(await pictureFiles(img, crop));
              setSrc(null);
            })
          }
        />
        {problem && <Notice tone="crit">{problem}</Notice>}
      </div>
    );
  }
  return (
    <div className="photo-editor">
      {inputs}
      <div className="photo-row">
        <Face userId={userId} name={name} size="large" className="photo-face" />
        <div className="photo-buttons">
          <Button size="small" variant={version ? 'secondary' : 'primary'} icon="upload" onClick={() => choose.current?.click()} disabled={busy} {...(autoFocus ? { 'data-autofocus': true } : {})}>
            {version ? 'Change photo' : 'Choose a photo'}
          </Button>
          {phone && (
            <Button size="small" variant="secondary" icon="camera" onClick={() => camera.current?.click()} disabled={busy}>
              Take a photo
            </Button>
          )}
          {version && remove && (
            <Button size="small" variant="ghost" icon="trash" onClick={() => void run(remove)} disabled={busy}>
              Remove
            </Button>
          )}
        </div>
      </div>
      {words.text && (
        <p className={`photo-phones ${words.tone}`}>
          {words.text}
          {words.tone === 'warn' && onRetry && (
            <>
              {' '}
              <button type="button" className="link-btn" onClick={() => void run(onRetry)} disabled={busy}>
                Try again
              </button>
            </>
          )}
        </p>
      )}
      {problem && <Notice tone="crit">{problem}</Notice>}
    </div>
  );
}
